import React, { createContext, ReactNode, useCallback, useState } from "react";
import { OmniboxSearchResult } from "../../generated/graphql";
import { useRecentSearches } from "./hooks/useRecentSearches";
import {
  INITIAL_SEARCH_QUERY_STATE,
  SearchQueryState,
  useSearchQueryState,
} from "./hooks/useSearchQueryState";
import {
  INITIAL_SEARCH_RESULTS_STATE,
  SearchResultsState,
  useSearchResultsState,
} from "./hooks/useSearchResultsState";

type SearchContextType = {
  searchQueryState: ReturnType<typeof useSearchQueryState>[0];
  searchQueryStateDispatch: ReturnType<typeof useSearchQueryState>[1];
  searchResultsState: ReturnType<typeof useSearchResultsState>[0];
  searchResultsDispatch: ReturnType<typeof useSearchResultsState>[1];
  getRecentSearches: ReturnType<typeof useRecentSearches>["getRecentSearches"];
  addRecentSearch: ReturnType<typeof useRecentSearches>["addRecentSearch"];
  removeRecentSearch: ReturnType<
    typeof useRecentSearches
  >["removeRecentSearch"];
  handleSearchResultClick: ((searchResult: OmniboxSearchResult) => void) | null;
  setHandleSearchResultClick: (
    passedMethod: ((searchResult: OmniboxSearchResult) => void) | null,
  ) => void;
  resetSearch: () => void;
};

const DEFAULT_SEARCH_CONTEXT_VALUE: SearchContextType = {
  searchQueryState: INITIAL_SEARCH_QUERY_STATE,
  searchQueryStateDispatch: () => {},
  searchResultsState: INITIAL_SEARCH_RESULTS_STATE,
  searchResultsDispatch: () => {},
  getRecentSearches: () => [],
  addRecentSearch: () => {},
  removeRecentSearch: () => {},
  handleSearchResultClick: null,
  setHandleSearchResultClick: () => {},
  resetSearch: () => {},
};

export const SearchContext = createContext<SearchContextType>(
  DEFAULT_SEARCH_CONTEXT_VALUE,
);

export const SearchContextProvider = ({
  children,
  initialQueryState,
  initialResultsState,
  initialHandleSearchResultClick = null,
}: {
  initialQueryState?: SearchQueryState;
  initialResultsState?: SearchResultsState;
  initialHandleSearchResultClick?:
    | ((searchResult: OmniboxSearchResult) => void)
    | null;
  children: ReactNode;
}) => {
  const [searchQueryState, searchQueryStateDispatch] =
    useSearchQueryState(initialQueryState);
  const [searchResultsState, searchResultsDispatch] =
    useSearchResultsState(initialResultsState);
  const { getRecentSearches, addRecentSearch, removeRecentSearch } =
    useRecentSearches();
  const [handleSearchResultClick, setHandleSearchResultClickInternal] =
    useState<((searchResult: OmniboxSearchResult) => void) | null>(
      () => initialHandleSearchResultClick,
    );

  const setHandleSearchResultClick = useCallback(
    (passedMethod: ((searchResult: OmniboxSearchResult) => void) | null) => {
      setHandleSearchResultClickInternal(() => passedMethod);
    },
    [setHandleSearchResultClickInternal],
  );

  const resetSearch = useCallback(() => {
    searchQueryStateDispatch({ type: "resetSearch" });
    searchResultsDispatch({ type: "resetSearch" });
  }, [searchQueryStateDispatch, searchResultsDispatch]);

  return (
    <SearchContext.Provider
      value={{
        searchQueryState,
        searchQueryStateDispatch,
        searchResultsState,
        searchResultsDispatch,
        getRecentSearches,
        addRecentSearch,
        removeRecentSearch,
        handleSearchResultClick,
        setHandleSearchResultClick,
        resetSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
